import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Phone, MapPin, Clock, Play } from "lucide-react";

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_b37c987e-a9dd-4e8c-a72c-31526fea54f1/artifacts/7yy88rse_1012%281%29.mov";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);

  useEffect(() => {
    // Vidéo désactivée sur mobile (perf) + Data Saver.
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const conn = navigator.connection || {};
    setShowVideo(!isMobile && !conn.saveData);
  }, []);

  useEffect(() => {
    if (!showVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => setNeedsTap(true));
  }, [showVideo]);

  const handleTap = () => {
    const v = videoRef.current;
    if (v) { v.muted = true; v.play().then(() => setNeedsTap(false)).catch(() => {}); }
  };

  const POSTER = "https://images.unsplash.com/photo-1633364468491-b0df288c7da7?w=1920&q=70&auto=format&fit=crop";

  return (
    <div data-testid="home-page">
      {/* HERO: video on desktop, animated poster image on mobile/slow */}
      <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
        {showVideo ? (
          <video
            ref={videoRef}
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay muted loop playsInline preload="auto"
            poster={POSTER}
            onLoadedMetadata={(e) => e.currentTarget.play().catch(() => setNeedsTap(true))}
          >
            <source src={VIDEO_URL} type="video/mp4" />
            <source src={VIDEO_URL} type="video/quicktime" />
          </video>
        ) : (
          <div
            data-testid="hero-poster"
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url('${POSTER}')`, animation: "kenBurns 18s ease-in-out infinite alternate" }}
          />
        )}
        <style>{`@keyframes kenBurns { 0% { transform: scale(1) } 100% { transform: scale(1.12) translate(-2%, -2%) } }`}</style>
        {/* Overlay gradient for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />

        {/* Text emerges from BEHIND the video — sits on top of overlay */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <p className="fade-up uppercase tracking-[0.5em] text-white/80 text-xs mb-6">花宮 · Morges</p>
          <h1 className="fade-up delay-100 font-display text-white text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.95] tracking-tight">
            Hanamiya<br /><em className="not-italic text-white/80">Ramen</em>
          </h1>
          <p className="fade-up delay-200 text-white/85 text-base sm:text-lg max-w-xl mt-8 leading-relaxed text-balance">
            L'authenticité japonaise au cœur de Morges.<br />Bouillons mijotés, nouilles fraîches, savoir-faire.
          </p>
          <div className="fade-up delay-300 flex flex-wrap gap-4 mt-12 justify-center">
            <Link
              to="/commander"
              data-testid="hero-order-btn"
              className="group bg-white text-primary px-8 py-4 rounded-sm hover:bg-white/90 transition flex items-center gap-2 font-medium"
            >
              Commander à emporter
              <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/reserver"
              data-testid="hero-reserve-btn"
              className="border border-white/40 text-white px-8 py-4 rounded-sm hover:bg-white/10 transition backdrop-blur"
            >
              Réserver une table
            </Link>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 text-white/60 text-xs uppercase tracking-[0.3em]">
          <div className="w-px h-12 bg-white/30 mx-auto mb-3 animate-pulse" />
          Découvrir
        </div>

        {/* Tap-to-play fallback (only desktop with blocked autoplay) */}
        {showVideo && needsTap && (
          <button
            type="button"
            onClick={handleTap}
            data-testid="hero-tap-to-play"
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/30"
            aria-label="Lancer la vidéo"
          >
            <span className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center text-white text-3xl">▶</span>
          </button>
        )}
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5 lg:col-start-1">
            <p className="uppercase tracking-[0.3em] text-xs text-primary mb-6">Notre histoire</p>
            <h2 className="font-display text-5xl lg:text-6xl text-foreground leading-[1] mb-8">
              Le ramen, comme<br /><em className="text-primary">au Japon</em>.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6 text-lg">
              Chez Hanamiya, chaque bol raconte une histoire. Nos bouillons mijotent pendant 14 heures, nos nouilles sont pétries chaque matin, et nos ingrédients sélectionnés avec soin.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Une parenthèse japonaise à deux pas de la gare de Morges — pour un déjeuner rapide, un dîner partagé, ou un plat à emporter.
            </p>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 h-[500px] lg:h-[640px]">
            <img src="https://customer-assets.emergentagent.com/job_morges-ramen/artifacts/09x26aj5_hana%20%20resto.jpg" alt="Salle du restaurant Hanamiya Ramen" className="col-span-4 row-span-4 object-cover w-full h-full rounded-sm hover:scale-[1.02] transition-transform duration-700" />
            <img src="https://customer-assets.emergentagent.com/job_morges-ramen/artifacts/g0hyy8qf_hana%20resto2.jpg" alt="Bar du restaurant" className="col-span-2 row-span-3 object-cover w-full h-full rounded-sm hover:scale-[1.02] transition-transform duration-700" />
            <img src="https://images.unsplash.com/photo-1633364468491-b0df288c7da7?w=600" alt="Bol de ramen" className="col-span-2 row-span-3 object-cover w-full h-full rounded-sm hover:scale-[1.02] transition-transform duration-700" />
            <img src="https://images.unsplash.com/photo-1706128998662-3d85339fb6c8?w=800" alt="Préparation ramen" className="col-span-4 row-span-2 object-cover w-full h-full rounded-sm hover:scale-[1.02] transition-transform duration-700" />
          </Reveal>
        </div>
      </section>

      {/* INFOS */}
      <section data-testid="info-section" className="bg-primary text-white seigaiha">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-3 gap-12">
          <Reveal delay={0}><InfoCard icon={<MapPin size={24} />} title="Adresse" lines={["Rue de la Gare 8", "1110 Morges, Suisse"]} /></Reveal>
          <Reveal delay={150}><InfoCard icon={<Phone size={24} />} title="Téléphone" lines={["021 802 05 93"]} link="tel:0218020593" /></Reveal>
          <Reveal delay={300}><InfoCard icon={<Clock size={24} />} title="Horaires" lines={["Lun · Mer–Dim : 11h–14h30", "& 18h–22h30", "Mardi · fermé"]} /></Reveal>
        </div>
      </section>

      {/* MAP + CTA */}
      <section data-testid="map-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-xs text-primary mb-6">Nous trouver</p>
            <h2 className="font-display text-5xl text-foreground mb-6">À deux minutes<br />de la gare.</h2>
            <p className="text-muted-foreground mb-8 leading-relaxed text-lg">
              Idéalement situés en plein centre de Morges, accessibles à pied depuis la gare CFF.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/commander" data-testid="cta-order" className="bg-primary text-white px-6 py-3 rounded-sm hover:bg-primary/90 transition">Commander</Link>
              <Link to="/reserver" data-testid="cta-reserve" className="border border-primary text-primary px-6 py-3 rounded-sm hover:bg-primary hover:text-white transition">Réserver</Link>
              <Link to="/menu" data-testid="cta-menu" className="border border-border text-foreground px-6 py-3 rounded-sm hover:bg-foreground hover:text-white transition">Voir le menu</Link>
            </div>
          </Reveal>
          <Reveal delay={200} className="rounded-sm overflow-hidden border border-border h-[400px]">
            <iframe
              data-testid="google-map"
              title="Hanamiya Ramen - Carte"
              src="https://www.google.com/maps?q=Rue+de+la+Gare+8,+1110+Morges&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon, title, lines, link }) {
  const Comp = link ? "a" : "div";
  return (
    <Comp href={link} className="block group">
      <div className="w-12 h-12 border border-white/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-white/10 transition">{icon}</div>
      <h3 className="font-display text-3xl mb-3">{title}</h3>
      {lines.map((l, i) => <p key={i} className="text-white/80 leading-relaxed">{l}</p>)}
    </Comp>
  );
}
